import { useEffect, useState } from "react";
import { useNavigate, useLocation } from "react-router-dom";
import { getUser } from "../../../utils/storage";
import { axiosInstance } from "../../../utils/axios";
import useModalStore from "../../../stores/useModalStore";
import {
  XMarkIcon,
  ArrowPathIcon,
  TrashIcon,
  ArrowLeftIcon,
} from "@heroicons/react/20/solid";
import AdminSidebar from "../../../components/AdminSidebar";
import PaginationV2 from "../../../components/PaginationV2";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import useDialogStore from "../../../stores/useDialogStore";
import EditableCell from "../../../components/EditableCell";
import { toast } from "react-toastify";
import EditableSelectCell from "../../../components/EditableSelectCell";
import ColumnSearch from "../../../components/ColumnSearch";
import EditableOptionsCell from "../../../components/EditableOptionsCell";
import dayjs from "dayjs";
import utc from "dayjs/plugin/utc";
import timezone from "dayjs/plugin/timezone";
import NoData from "../../../components/NoData";
import LoadingComponent from "../../../components/LoadingComponent";
import Button from "../../../components/Button";
import EditSerialNumberCell from "../../../components/EditSerialNumberCell";

dayjs.extend(utc);
dayjs.extend(timezone);

export default function TrashDevices() {
  const { serialNo, setSerialNo } = useModalStore();
  const { openDialog, closeDialog } = useDialogStore();
  const [selectedDevices, setSelectedDevices] = useState(new Set());
  const [selectAll, setSelectAll] = useState(false);

  const sortConfig = {
    key: null,
    direction: "desc",
  };

  const user = getUser();
  const location = useLocation();
  const navigate = useNavigate();
  const queryClient = useQueryClient();

  const {
    data: devicesData,
    isPending,
    refetch,
  } = useQuery({
    queryKey: ["devices", location.pathname, location.search],
    queryFn: async () => {
      const params = new URLSearchParams(location.search);
      // Ensure sort parameters are properly formatted
      const sortBy = params.get("sortBy");
      const order = params.get("order");

      if (sortBy) {
        params.set("sortBy", sortBy);
        params.set("order", order || "asc");
      }

      const response = await axiosInstance.get(
        `${
          window.REACT_APP_API_ENDPOINT
        }/trash/deleted-devices?${params.toString()}`
      );
      return response.data;
    },
  });

  const restoreDeviceMutation = useMutation({
    mutationFn: (id) =>
      axiosInstance.post(`${window.REACT_APP_API_ENDPOINT}/trash/restore`, {
        id,
        type: "devices",
      }),
    onSuccess: () => {
      queryClient.invalidateQueries([
        "devices",
        location.pathname,
        location.search,
      ]);
      closeDialog();
      toast.success("Device restored successfully!");
    },
    onError: (error) => {
      toast.error("Failed to restore device. Please try again.");
      console.error("Delete device error:", error);
    },
  });

  const bulkRestoreMutation = useMutation({
    mutationFn: (ids) =>
      axiosInstance.post(`${window.REACT_APP_API_ENDPOINT}/trash/restore`, {
        ids,
        type: "devices",
      }),
    onSuccess: () => {
      queryClient.invalidateQueries([
        "devices",
        location.pathname,
        location.search,
      ]);
      setSelectedDevices(new Set());
      setSelectAll(false);
      toast.success("Devices restored successfully!");
    },
    onError: (error) => {
      toast.error("Failed to restore some devices. Please try again.");
      console.error("Bulk restore error:", error);
    },
  });

  const bulkDeletePermanentlyMutation = useMutation({
    mutationFn: (ids) =>
      axiosInstance.post(
        `${window.REACT_APP_API_ENDPOINT}/trash/delete-permanently`,
        {
          ids,
          type: "devices",
        }
      ),
    onSuccess: () => {
      queryClient.invalidateQueries([
        "devices",
        location.pathname,
        location.search,
      ]);
      setSelectedDevices(new Set());
      setSelectAll(false);
      toast.success("Devices deleted permanently!");
    },
    onError: (error) => {
      toast.error(
        "Failed to delete some devices permanently. Please try again."
      );
      console.error("Bulk delete permanently error:", error);
    },
  });

  const editCellMutation = useMutation({
    mutationFn: ({ deviceId, field, value }) =>
      axiosInstance.patch(
        `${window.REACT_APP_API_ENDPOINT}/devices/patch/${deviceId}`,
        { [field]: value, user_id: user.id }
      ),
    onSuccess: () => {
      refetch();
      toast.success("Device updated successfully!");
    },
    onError: (error) => {
      toast.error("Failed to update device. Please try again.");
      console.error("Update device error:", error);
    },
  });

  useEffect(() => {
    if (serialNo != null) {
      alert("Scanned and filtered.");
      const params = new URLSearchParams(location.search);
      params.set("serial_no", serialNo);
      navigate(`${location.pathname}?${params.toString()}`);
      setSerialNo(null);
    }
  }, [serialNo]);

  const handleColumnSearch = (columnKey, query) => {
    const params = new URLSearchParams(location.search);
    if (query) {
      params.set(columnKey, query);
    } else {
      params.delete(columnKey);
    }
    params.delete("page"); // Reset to first page when searching
    navigate(`${location.pathname}?${params.toString()}`);
    // Force refetch when filters change
    queryClient.invalidateQueries(["devices"]);
  };

  const handleColumnSort = (column, direction) => {
    const params = new URLSearchParams(location.search);
    const currentSortBy = params.get("sortBy");
    const currentOrder = params.get("order");

    // If clicking the same column, toggle direction
    if (currentSortBy === column) {
      const newDirection = currentOrder === "asc" ? "desc" : "asc";
      params.set("order", newDirection);
    } else {
      // If clicking a new column, set it to ascending
      params.set("sortBy", column);
      params.set("order", "desc");
    }

    // Ensure we're using the correct parameter names for the API
    const sortBy = params.get("sortBy");
    const order = params.get("order");

    // Update the URL with the correct parameters
    const newParams = new URLSearchParams();
    if (sortBy) newParams.set("sortBy", sortBy);
    if (order) newParams.set("order", order);

    // Preserve other existing parameters
    for (const [key, value] of params.entries()) {
      if (key !== "sortBy" && key !== "order") {
        newParams.set(key, value);
      }
    }

    navigate(`${location.pathname}?${newParams.toString()}`);
    queryClient.invalidateQueries(["devices"]);
  };

  const handleClearFilters = () => {
    navigate(location.pathname);
    // Force refetch when filters are cleared
    queryClient.invalidateQueries(["devices"]);
  };

  // Selection handlers
  const handleSelectDevice = (deviceId) => {
    const newSelected = new Set(selectedDevices);
    if (newSelected.has(deviceId)) {
      newSelected.delete(deviceId);
    } else {
      newSelected.add(deviceId);
    }
    setSelectedDevices(newSelected);
    setSelectAll(newSelected.size === devices.length);
  };

  const handleSelectAll = () => {
    if (selectAll) {
      setSelectedDevices(new Set());
      setSelectAll(false);
    } else {
      setSelectedDevices(new Set(devices.map((device) => device.id)));
      setSelectAll(true);
    }
  };

  // Bulk action handlers
  const handleBulkRestore = () => {
    if (selectedDevices.size === 0) {
      toast.warning("Please select at least one device to restore.");
      return;
    }

    openDialog({
      title: "Restore Multiple Devices",
      content: (
        <div className="flex flex-col gap-4">
          <p className="text-lg">
            Are you sure you want to restore {selectedDevices.size} device
            {selectedDevices.size > 1 ? "s" : ""}?
          </p>
          <div className="flex items-center justify-end gap-2">
            <Button variant="light" size="sm" onClick={() => closeDialog()}>
              Cancel
            </Button>
            <Button
              variant="outline-success"
              size="sm"
              icon={ArrowPathIcon}
              onClick={() => {
                bulkRestoreMutation.mutate(Array.from(selectedDevices));
                closeDialog();
              }}
            >
              Restore All
            </Button>
          </div>
        </div>
      ),
    });
  };

  const handleBulkDeletePermanently = () => {
    if (selectedDevices.size === 0) {
      toast.warning("Please select at least one device to delete permanently.");
      return;
    }

    openDialog({
      title: "Delete Permanently",
      content: (
        <div className="flex flex-col gap-4">
          <p className="text-lg text-red-600 font-semibold">
            ⚠️ WARNING: This action cannot be undone!
          </p>
          <p className="text-lg">
            Are you sure you want to permanently delete {selectedDevices.size}{" "}
            device{selectedDevices.size > 1 ? "s" : ""}? This action will
            completely remove the device{selectedDevices.size > 1 ? "s" : ""}{" "}
            from the database.
          </p>
          <div className="flex items-center justify-end gap-2">
            <Button
              variant="light"
              size="sm"
              onClick={() => closeDialog()}
              className=""
            >
              Cancel
            </Button>
            <Button
              variant="danger"
              size="sm"
              icon={TrashIcon}
              onClick={() => {
                bulkDeletePermanentlyMutation.mutate(
                  Array.from(selectedDevices)
                );
                closeDialog();
              }}
              className=""
            >
              Delete Permanently
            </Button>
          </div>
        </div>
      ),
    });
  };

  const handleRestore = (id) => {
    openDialog({
      title: "Restore Device",
      content: (
        <div className="flex flex-col gap-4">
          <p className="text-lg ">
            Are you sure you want to restore this device?
          </p>
          <div className="flex items-center justify-end gap-2">
            <Button variant="light" size="sm" onClick={() => closeDialog()}>
              Cancel
            </Button>
            <Button
              variant="outline-success"
              size="sm"
              icon={<ArrowPathIcon className="h-4 w-4" />}
              onClick={() => restoreDeviceMutation.mutate(id)}
            >
              Restore
            </Button>
          </div>
        </div>
      ),
    });
  };

  const handleDeletePermanently = (id) => {
    openDialog({
      title: "Delete Permanently",
      content: (
        <div className="flex flex-col gap-4">
          <p className="text-lg text-red-600 font-semibold">
            ⚠️ WARNING: This action cannot be undone!
          </p>
          <p className="text-lg">
            Are you sure you want to permanently delete this device? This action
            will completely remove the device from the database.
          </p>
          <div className="flex items-center justify-end gap-2">
            <Button variant="light" size="sm" onClick={() => closeDialog()}>
              Cancel
            </Button>
            <Button
              variant="danger"
              size="sm"
              icon={<TrashIcon className="h-4 w-4" />}
              onClick={() => {
                // Use the individual delete endpoint
                axiosInstance
                  .post(
                    `${window.REACT_APP_API_ENDPOINT}/trash/delete-permanently`,
                    {
                      id,
                      type: "devices",
                    }
                  )
                  .then(() => {
                    queryClient.invalidateQueries([
                      "devices",
                      location.pathname,
                      location.search,
                    ]);
                    setSelectedDevices(new Set());
                    setSelectAll(false);
                    closeDialog();
                    toast.success("Device deleted permanently!");
                  })
                  .catch((error) => {
                    toast.error(
                      "Failed to delete device permanently. Please try again."
                    );
                    console.error("Delete permanently error:", error);
                  });
              }}
            >
              Delete
            </Button>
          </div>
        </div>
      ),
    });
  };

  const brands = useQuery({
    queryKey: ["brands"],
    queryFn: () =>
      axiosInstance.get(`${window.REACT_APP_API_ENDPOINT}/device-brands`),
    select: (data) => data.data.data.map(({ id, name }) => ({ id, name })),
  }).data;

  const models = useQuery({
    queryKey: ["models"],
    queryFn: () =>
      axiosInstance.get(
        `${window.REACT_APP_API_ENDPOINT}/device-models?type=all`
      ),
    select: (data) =>
      data.data.data.data.map(({ id, name, device_brand_id }) => ({
        id,
        name,
        device_brand_id,
      })),
  }).data;

  const devices = devicesData?.devices?.data || [];
  const paginationData = {
    current_page: devicesData?.devices?.current_page || 1,
    last_page: devicesData?.devices?.last_page || 1,
    next_page_url: devicesData?.devices?.next_page_url,
    prev_page_url: devicesData?.devices?.prev_page_url,
  };

  return (
    <div className="px-4 sm:px-6 lg:px-8 bg-bgBlue">
      <div className="sm:flex flex-col sm:items-start">
        <div className="w-full flex justify-between py-4 px-4 bg-white rounded-lg">
          <h3 className="font-bold text-2xl">👋 Hello {user.name}</h3>
        </div>
      </div>
      <div className="mt-8 flow-root bg-white rounded-lg">
        <div className="-mx-4 -my-2 sm:-mx-6 lg:-mx-8">
          <div className="inline-block lg:min-w-full max-w-full py-2 align-middle sm:px-6 lg:px-8">
            <div className="w-full flex flex-col lg:flex-row justify-between items-center py-3 px-4 bg-white rounded-lg">
              <div className="flex flex-col gap-2">
                <h4 className="font-semibold text-2xl mb-2 lg:mb-0">
                  Deleted Devices
                </h4>
                {selectedDevices.size > 0 && (
                  <div className="flex items-center gap-4 text-sm text-gray-600">
                    <span>
                      {selectedDevices.size} device
                      {selectedDevices.size > 1 ? "s" : ""} selected
                    </span>
                    <Button
                      variant="light"
                      size="xs"
                      onClick={() => {
                        setSelectedDevices(new Set());
                        setSelectAll(false);
                      }}
                    >
                      Clear Selection
                    </Button>
                  </div>
                )}
              </div>
              <div className="flex flex-col gap-2 lg:flex-row items-center">
                {selectedDevices.size > 0 && (
                  <>
                    <Button
                      variant="outline-success"
                      size="sm"
                      icon={ArrowPathIcon}
                      onClick={handleBulkRestore}
                      disabled={bulkRestoreMutation.isPending}
                      loading={bulkRestoreMutation.isPending}
                    >
                      Restore Selected ({selectedDevices.size})
                    </Button>
                    <Button
                      variant="outline-danger"
                      size="sm"
                      icon={TrashIcon}
                      onClick={handleBulkDeletePermanently}
                      disabled={bulkDeletePermanentlyMutation.isPending}
                      loading={bulkDeletePermanentlyMutation.isPending}
                      className=""
                    >
                      Delete Permanently ({selectedDevices.size})
                    </Button>
                  </>
                )}
                <Button
                  variant="secondary"
                  size="sm"
                  icon={<XMarkIcon className="h-4 w-4" />}
                  onClick={handleClearFilters}
                  className="mb-2 lg:mb-0"
                >
                  Clear Filters
                </Button>
              </div>
            </div>
            <div className="overflow-x-auto">
              <table className="lg:min-w-full divide-y divide-gray-300">
                <thead className="bg-black">
                  <tr>
                    <th className="px-4 py-4 text-left text-white text-sm">
                      <div className="flex items-center gap-2">
                        <input
                          type="checkbox"
                          checked={selectAll}
                          onChange={handleSelectAll}
                          className="h-4 w-4 text-blue-600 focus:ring-blue-500 border-gray-300 rounded"
                        />
                      </div>
                    </th>
                    <th className="px-4 py-4 text-left text-white text-sm">
                      <div className="flex items-center gap-2">
                        <ColumnSearch
                          columnName="Unique ID"
                          columnKey="unique_id"
                          onSearch={handleColumnSearch}
                          onSort={handleColumnSort}
                          sortConfig={sortConfig}
                          placeholder="Search by Unique ID..."
                        />
                      </div>
                    </th>
                    <th className="px-4 py-4 text-left text-white text-sm">
                      <div className="flex items-center gap-2">
                        <ColumnSearch
                          columnName="Brand"
                          columnKey="brand"
                          onSearch={handleColumnSearch}
                          onSort={handleColumnSort}
                          sortConfig={sortConfig}
                          placeholder="Search by Brand..."
                        />
                      </div>
                    </th>
                    <th className="px-4 py-4 text-left text-white text-sm">
                      <div className="flex items-center gap-2">
                        <ColumnSearch
                          columnName="Model"
                          columnKey="model"
                          onSearch={handleColumnSearch}
                          onSort={handleColumnSort}
                          sortConfig={sortConfig}
                          placeholder="Search by Model..."
                        />
                      </div>
                    </th>
                    <th className="px-4 py-4 text-left text-white text-sm">
                      <div className="flex items-center gap-2">
                        <ColumnSearch
                          columnName="Serial"
                          columnKey="serial_no"
                          onSearch={handleColumnSearch}
                          onSort={handleColumnSort}
                          sortConfig={sortConfig}
                          placeholder="Search by Serial..."
                        />
                      </div>
                    </th>
                    <th className="px-4 py-4 text-left text-white text-sm">
                      <div className="flex items-center gap-2">
                        <ColumnSearch
                          columnName="Counter Black"
                          columnKey="counter_black"
                          onSearch={handleColumnSearch}
                          onSort={handleColumnSort}
                          sortConfig={sortConfig}
                          placeholder="Search by Counter Black..."
                        />
                      </div>
                    </th>
                    <th className="px-4 py-4 text-left text-white text-sm">
                      <div className="flex items-center gap-2">
                        <ColumnSearch
                          columnName="Counter Color"
                          columnKey="counter_color"
                          onSearch={handleColumnSearch}
                          onSort={handleColumnSort}
                          sortConfig={sortConfig}
                          placeholder="Search by Counter Color..."
                        />
                      </div>
                    </th>
                    <th className="px-4 py-4 text-left text-white text-sm">
                      <div className="flex items-center gap-2">
                        <ColumnSearch
                          columnName="Total Counter"
                          columnKey="total_counter"
                          onSearch={handleColumnSearch}
                          onSort={handleColumnSort}
                          sortConfig={sortConfig}
                          placeholder="Search by Total Counter..."
                        />
                      </div>
                    </th>
                    <th className="px-4 py-4 text-left text-white text-sm">
                      <div className="flex items-center gap-2">
                        <ColumnSearch
                          columnName="Palette"
                          columnKey="palette"
                          onSearch={handleColumnSearch}
                          onSort={handleColumnSort}
                          sortConfig={sortConfig}
                          placeholder="Search by Palette..."
                        />
                      </div>
                    </th>
                    <th className="px-4 py-4 text-left text-white text-sm">
                      <div className="flex items-center gap-2">
                        <ColumnSearch
                          columnName="Date Registered"
                          columnKey="created_at"
                          onSearch={handleColumnSearch}
                          onSort={handleColumnSort}
                          sortConfig={sortConfig}
                          isDate={true}
                          placeholder="Search by Date..."
                        />
                      </div>
                    </th>
                    <th className="px-4 py-4 text-left text-white text-sm">
                      Options
                    </th>
                    <th className="px-4 py-4 text-left text-white text-sm">
                      Comment
                    </th>
                    <th className="px-4 py-4 text-left text-white text-sm">
                      Actions
                    </th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-gray-200 bg-white">
                  {isPending ? (
                    <tr>
                      <td colSpan={13} className="text-center">
                        <LoadingComponent />
                      </td>
                    </tr>
                  ) : devices.length === 0 ? (
                    <tr>
                      <td colSpan={13} className="text-center">
                        <NoData message="No devices found." />
                      </td>
                    </tr>
                  ) : (
                    devices?.map((device, index) => (
                      <tr
                        key={device.id}
                        className={`divide-y divide-gray-200 ${
                          index % 2 === 0 ? "bg-white" : "bg-gray-100"
                        }`}
                      >
                        <td className="whitespace-nowrap p-3 text-sm text-black">
                          <input
                            type="checkbox"
                            checked={selectedDevices.has(device.id)}
                            onChange={() => handleSelectDevice(device.id)}
                            className="h-4 w-4 text-blue-600 focus:ring-blue-500 border-gray-300 rounded"
                          />
                        </td>
                        <td className="whitespace-nowrap p-3 text-sm text-black">
                          <div className="mt-1 text-black">
                            {device.unique_id}
                          </div>
                        </td>
                        <td className="whitespace-nowrap p-3 text-sm sm:pl-0">
                          <div className="flex items-center pl-4">
                            {brands && (
                              <EditableSelectCell
                                value={device.device_brand_id}
                                displayValue={device.brand}
                                id={device.id}
                                options={brands}
                                field="brand"
                                onSave={(data) => {
                                  editCellMutation.mutate({
                                    deviceId: data.id,
                                    field: "device_brand_id",
                                    value: data.value,
                                  });
                                }}
                              />
                            )}
                          </div>
                        </td>
                        <td className="whitespace-nowrap p-3 text-sm text-black">
                          {models && (
                            <EditableSelectCell
                              value={device.device_model_id}
                              displayValue={device.model}
                              id={device.id}
                              options={models.filter(
                                (model) =>
                                  model.device_brand_id ===
                                  device.device_brand_id
                              )}
                              field="device_model_id"
                              onSave={(data) =>
                                editCellMutation.mutate({
                                  deviceId: data.id,
                                  field: "device_model_id",
                                  value: data.value,
                                })
                              }
                            />
                          )}
                        </td>
                        <td className="whitespace-nowrap p-3 text-sm text-black">
                          <EditSerialNumberCell
                            value={device.serial_no || "—"}
                            displayValue={device.serial_no || "—"}
                            id={device.id}
                            field="serial_no"
                            onSave={(data) =>
                              editCellMutation.mutate({
                                deviceId: data.id,
                                field: "serial_no",
                                value: data.value,
                              })
                            }
                          />
                        </td>
                        <td className="whitespace-nowrap p-3 text-sm text-black">
                          <EditableCell
                            value={device.counter_black || "—"}
                            displayValue={device.counter_black || "—"}
                            id={device.id}
                            field="counter_black"
                            onSave={(data) =>
                              editCellMutation.mutate({
                                deviceId: data.id,
                                field: "counter_black",
                                value: data.value,
                              })
                            }
                          />
                        </td>
                        <td className="whitespace-nowrap p-3 text-sm text-black">
                          <EditableCell
                            value={device.counter_color || "—"}
                            displayValue={device.counter_color || "—"}
                            id={device.id}
                            field="counter_color"
                            onSave={(data) =>
                              editCellMutation.mutate({
                                deviceId: data.id,
                                field: "counter_color",
                                value: data.value,
                              })
                            }
                          />
                        </td>
                        <td className="whitespace-nowrap p-3 text-sm text-black">
                          <EditableCell
                            value={device.total_counter || "—"}
                            displayValue={device.total_counter || "—"}
                            id={device.id}
                            field="total_counter"
                            onSave={(data) =>
                              editCellMutation.mutate({
                                deviceId: data.id,
                                field: "total_counter",
                                value: data.value,
                              })
                            }
                          />
                        </td>
                        <td className="whitespace-nowrap p-3 text-sm text-black">
                          <EditableCell
                            value={device.palette || "—"}
                            displayValue={device.palette || "—"}
                            id={device.id}
                            field="palette"
                            onSave={(data) =>
                              editCellMutation.mutate({
                                deviceId: data.id,
                                field: "palette",
                                value: data.value,
                              })
                            }
                          />
                        </td>
                        <td className="whitespace-nowrap p-3 text-sm text-black">
                          {dayjs.utc(device.created_at).format("DD-MM-YYYY")}
                        </td>

                        <EditableOptionsCell
                          deviceId={device.id}
                          deviceOptions={device.device_options}
                        />

                        <td className="whitespace-nowrap p-3 text-sm text-black">
                          <EditableCell
                            value={device.comment || ""}
                            displayValue={device.comment || ""}
                            id={device.id}
                            field="comment"
                            onSave={(data) =>
                              editCellMutation.mutate({
                                deviceId: data.id,
                                field: "comment",
                                value: data.value,
                              })
                            }
                          />
                        </td>
                        <td className="whitespace-nowrap p-3 text-sm sm:pl-0">
                          <div className="flex gap-2">
                            <Button
                              variant="outline-success"
                              size="sm"
                              icon={<ArrowPathIcon className="h-4 w-4" />}
                              onClick={() => handleRestore(device.id)}
                            >
                              Restore
                            </Button>
                            <Button
                              variant="outline-danger"
                              size="sm"
                              icon={<TrashIcon className="h-4 w-4" />}
                              onClick={() => handleDeletePermanently(device.id)}
                            >
                              Delete
                            </Button>
                          </div>
                        </td>
                      </tr>
                    ))
                  )}
                </tbody>
              </table>
            </div>
            <PaginationV2
              current_page={paginationData.current_page}
              last_page={paginationData.last_page}
              next_page_url={paginationData.next_page_url}
              prev_page_url={paginationData.prev_page_url}
            />
          </div>
        </div>
      </div>
    </div>
  );
}
